import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControl,
  FormControlLabel,
  Divider,
  Grid2
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DentalInvestigationParameters } from "../Utility/Constants";

// FDI numbering for adult teeth (permanent teeth)
const FDI_TEETH = [
  // Upper right (18-11)
  18, 17, 16, 15, 14, 13, 12, 11,
  // Upper left (21-28)
  21, 22, 23, 24, 25, 26, 27, 28,
  // Lower left (38-31)
  38, 37, 36, 35, 34, 33, 32, 31,
  // Lower right (41-48)
  41, 42, 43, 44, 45, 46, 47, 48,
];

const UPPER_TOOTH_PATHS = {
  incisor: "M18 2 Q12 0 6 2 Q2 8 6 18 Q12 22 18 18 Q22 8 18 2 Z",
  canine: "M18 2 Q12 0 6 2 Q2 10 10 22 Q12 24 14 22 Q22 10 18 2 Z",
  premolar: "M18 2 Q12 0 6 2 Q2 10 8 18 Q12 22 18 18 Q24 10 18 2 Z",
  molar: "M18 2 Q12 0 6 2 Q2 10 6 18 Q8 22 12 18 Q16 22 18 18 Q24 10 18 2 Z",
};

const LOWER_TOOTH_PATHS = {
  incisor: "M18 23 Q12 25 6 23 Q2 17 6 7 Q12 3 18 7 Q22 17 18 23 Z",
  canine: "M18 23 Q12 25 6 23 Q2 15 10 3 Q12 1 14 3 Q22 15 18 23 Z",
  premolar: "M18 23 Q12 25 6 23 Q2 15 8 7 Q12 3 18 7 Q24 15 18 23 Z",
  molar: "M18 23 Q12 25 6 23 Q2 15 6 7 Q8 5 12 7 Q16 5 18 7 Q24 15 18 23 Z",
};

const xrayTypes = ["Bitewing", "Periapical", "Occlusal", "Panoramic"];

const getToothType = (tooth) => {
  const incisor = [11, 12, 21, 22, 31, 32, 41, 42];
  const canine = [13, 23, 33, 43];
  const premolar = [14, 15, 24, 25, 34, 35, 44, 45];
  if (incisor.includes(tooth)) return "incisor";
  if (canine.includes(tooth)) return "canine";
  if (premolar.includes(tooth)) return "premolar";
  return "molar";
};

const InvestigationToothChart = ({ investigations, setInvestigations }) => {
  const toothWidth = 36;
  const toothHeight = 48;
  const gap = 0;
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [currentParameters, setCurrentParameters] = useState(DentalInvestigationParameters); 
  // const [currentInvestigations, setCurrentInvestigations] = useState({
  //   xray: { selected: false, types: [], customText: "" },
  //   rvg: { selected: false, customText: "" },
  //   cbct: { selected: false, customText: "" },
  //   opg: { selected: false, lateralCeph: false, customText: "" },
  // });

  const handleToothClick = (tooth) => {
    setSelectedTooth(tooth);
    //setCurrentParameters(currentParameters.find((item) => item.toothNumber === tooth)?.parameters || []);
  };

  const handleSaveInvestigations = () => {
    if (!selectedTooth) return;
    
    if (!investigations.some((item) => item.toothNumber === selectedTooth)) {
      setInvestigations((prev) => [
        ...prev,
        {
          toothNumber: selectedTooth,
          parameters: currentParameters,
        },
      ]);
    } else {
      // Update existing tooth details
      setInvestigations((prev) => {
        const updated = prev.map((item) =>
          item.toothNumber === selectedTooth
            ? {
                ...item,
                parameters: currentParameters,
              }
            : item
        );
        return updated;
      });
    } 

      setSelectedTooth(null);
      setCurrentParameters(DentalInvestigationParameters);
  }
    

  const handleDeleteInvestigations = (tooth) => {
    setInvestigations((prev) => {
      const updated = prev.filter((item) => item.toothNumber !== tooth);
      return updated;
    });
  };

  // const getToothInvestigationStatus = (tooth) => {
  //   const hasXray = investigations.toothSpecificXrays?.[tooth]?.length > 0;
  //   const hasRvg = investigations.rvg?.teeth.includes(tooth.toString());
  //   const hasCbct = investigations.cbct?.teeth.includes(tooth.toString());
  //   const hasOpg =
  //     investigations?.opg?.teeth?.includes(tooth.toString()) || false;
  //   return hasXray || hasRvg || hasCbct || hasOpg;
  // };

  const renderTeeth = () => {
    const upper = FDI_TEETH.slice(0, 16);
    const lower = FDI_TEETH.slice(16);

    return (
      <>
        {/* Upper teeth */}
        {upper.map((tooth, i) => (
          <g key={tooth}>
            <Tooltip title={`Tooth ${tooth} - Click to add investigations`}>
              <path
                d={UPPER_TOOTH_PATHS[getToothType(tooth)]}
                transform={`translate(${i * (toothWidth + gap)}, 0) scale(1.5)`}
                fill={
                  investigations.find((item) => item.toothNumber === tooth)
                    ? "#ffab91"
                    : "#f3e5f5"
                }
                stroke="#7b1fa2"
                strokeWidth={selectedTooth === tooth ? 4 : 2}
                style={{ cursor: "pointer" }}
                onClick={() => handleToothClick(tooth)}
              />
            </Tooltip>
            <text
              x={i * (toothWidth + gap) + toothWidth / 2}
              y={toothHeight / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#7b1fa2"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {tooth}
            </text>
          </g>
        ))}
        {/* Lower teeth */}
        {lower.map((tooth, i) => (
          <g key={tooth}>
            <Tooltip title={`Tooth ${tooth} - Click to add investigations`}>
              <path
                d={LOWER_TOOTH_PATHS[getToothType(tooth)]}
                transform={`translate(${i * (toothWidth + gap)}, ${
                  toothHeight + gap
                }) scale(1.5)`}
                fill={
                  investigations.find((item) => item.toothNumber === tooth)
                    ? "#ffab91"
                    : "#f3e5f5"
                }
                stroke="#7b1fa2"
                strokeWidth={selectedTooth === tooth ? 4 : 2}
                style={{ cursor: "pointer" }}
                onClick={() => handleToothClick(tooth)}
              />
            </Tooltip>
            <text
              x={i * (toothWidth + gap) + toothWidth / 2}
              y={toothHeight + gap + toothHeight / 2}
              textAnchor="middle"
              fontSize="14"
              fill="#7b1fa2"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {tooth}
            </text>
          </g>
        ))}
      </>
    );
  };

  // const teethWithInvestigations = FDI_TEETH.filter((tooth) =>
  //   getToothInvestigationStatus(tooth)
  // );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          width: (toothWidth + gap) * 16 + 25,
          height: (toothHeight + gap) * 2 + 25,
          overflowX: "auto",
          border: "1px solid #ddd",
          borderRadius: 2,
          background: "#fafafa",
          py: 1,
          mx: "auto",
        }}
      >
        <svg
          width={(toothWidth + gap) * 16}
          height={(toothHeight + gap) * 2}
          style={{ display: "block" }}
        >
          {renderTeeth()}
        </svg>
      </Box>

      {investigations?.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Teeth wise investigation details
          </Typography>
          <Grid container spacing={2}>
            {investigations.map((investigationDetails) => (
              <Grid key={investigationDetails.toothNumber} item xs={6} sm={4} md={3}>
                <Card variant="outlined" sx={{ position: "relative" }}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteInvestigations(investigationDetails.toothNumber)}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <CardContent sx={{ p: 1.5 }}>
                    <Typography color="secondary" variant="subtitle2">
                      Tooth {investigationDetails.toothNumber}
                    </Typography>
                    <Box sx={{ }}>
                      {investigationDetails.parameters.map((param) => (
                        param.selected && (<>
                        <Typography key={param.value} variant="body2" sx={{ fontSize: "0.75rem" }}>
                          <strong>Method:</strong> {param.displayValue}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          {param?.subType && (<>
                            {Array.isArray(param?.subType) &&
                              param?.subType?.filter((sub) => sub?.selected).length > 0 && (
                                <>
                                  <strong>Types:</strong>{" "}
                                  {param?.subType
                                    ?.filter((sub) => sub.selected)
                                    ?.map((sub) => sub.displayValue)
                                    ?.join(", ")}
                                  <br />
                                </>
                              )}
                            {typeof param?.subType === "object" && !Array.isArray(param?.subType) && (
                               <>
                                <strong>Types:</strong>{" "}
                                {param?.subType?.displayValue || "Not Applicable"}
                                </>
                            )}
                            </>)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          {param.notes && (<>
                            <strong>Notes:</strong> {param.notes}
                          </>)}
                        </Typography>
                        </>
                        )
                      ))}
                      
                      </Box>
                    


                    {/* X-ray details */}
                    {/* {investigations.toothSpecificXrays?.[tooth]?.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          <strong>X-rays:</strong>{" "}
                          {investigations.toothSpecificXrays[tooth].join(", ")}
                        </Typography>
                        {investigations.toothSpecificCustomText?.[tooth]
                          ?.xray && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            {
                              investigations.toothSpecificCustomText[tooth].xray
                            }
                          </Typography>
                        )}
                      </Box>
                    )} */}

                    {/* RVG details */}
                    {/* {investigations.rvg?.teeth.includes(tooth.toString()) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          <strong>RVG:</strong> Required
                        </Typography>
                        {investigations.toothSpecificCustomText?.[tooth]
                          ?.rvg && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            {investigations.toothSpecificCustomText[tooth].rvg}
                          </Typography>
                        )}
                      </Box>
                    )} */}

                    {/* CBCT details */}
                    {/* {investigations.cbct?.teeth.includes(tooth.toString()) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          <strong>CBCT:</strong> Required
                        </Typography>
                        {investigations.toothSpecificCustomText?.[tooth]
                          ?.cbct && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            {investigations.toothSpecificCustomText[tooth].cbct}
                          </Typography>
                        )}
                      </Box>
                    )} */}

                    {/* OPG details */}
                    {/* {investigations.opg?.teeth?.includes(tooth.toString()) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                          <strong>OPG:</strong> Required
                          {investigations.opg?.lateralCeph && " + Lateral Ceph"}
                        </Typography>
                        {investigations.toothSpecificCustomText?.[tooth]
                          ?.opg && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            {investigations.toothSpecificCustomText[tooth].opg}
                          </Typography>
                        )}
                      </Box>
                    )} */}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Investigation Selection Dialog */}
      <Dialog
  open={selectedTooth}
  onClose={() => setSelectedTooth(null)}
  maxWidth="md"
  fullWidth
  PaperProps={{
    sx: {
      height: '80vh', // Fixed height
      maxHeight: '80vh',
    }
  }}
>
  <DialogTitle>
    Investigations for Tooth {selectedTooth}
  </DialogTitle>
  <DialogContent dividers sx={{ 
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555',
    },
  }}>
    <Grid2 container spacing={3}>
      {/* X-Ray Section */}
      <Grid2 size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={currentParameters.some((param) => param.value === "XRay" && param?.selected)}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "XRay" ? { ...param, selected: e.target.checked } : param
                  );
                  return updated;
                })
              }
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight="bold">
              X-Ray
            </Typography>
          }
        />

        {currentParameters.find((param) => param.value === "XRay")?.selected && (
          <Box
            sx={{
              ml: 4,
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              X-Ray Types
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                options={xrayTypes}
                disableCloseOnSelect
                value={currentParameters.find((param) => param.value === "XRay")?.subType?.filter((type) => type?.selected === true)?.map((type) => type.displayValue) || []}
                onChange={(event, newValue) => {
                  setCurrentParameters((prev) => {
                    const updated = prev.map((param) => {
                      if (param.value === "XRay") {
                        // Create subType array based on xrayTypes and selected values
                        const updatedSubType = xrayTypes.map((type) => ({
                          displayValue: type,
                          selected: newValue.includes(type)
                        }));
                        return { ...param, subType: updatedSubType };
                      }
                      return param;
                    });
                    return updated;
                  });
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                      color="primary"
                    />
                    {option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select X-Ray types"
                    variant="outlined"
                    size="small"
                  />
                )}
              />
            </FormControl>
            <TextField
              label="Additional X-Ray Details"
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              size="small"
              value={currentParameters.find((param) => param.value === "XRay")?.notes || ""}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "XRay"
                      ? { ...param, notes: e.target.value }
                      : param
                  );
                  return updated;
                })
              }
              placeholder="Enter additional details or custom X-Ray requirements..."
            />
          </Box>
        )}
      </Grid2>

      {/* RVG Section */}
      <Grid2 size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={currentParameters.some((param) => param.value === "RVG" && param?.selected)}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "RVG" ? { ...param, selected: e.target.checked } : param
                  );
                  return updated;
                })
              }
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight="bold">
              RVG (Radiovisiography)
            </Typography>
          }
        />

        {currentParameters.find((param) => param.value === "RVG")?.selected && (
          <Box
            sx={{
              ml: 4,
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              RVG Details
            </Typography>
            <TextField
              label="Additional RVG Notes"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={currentParameters.find((param) => param.value === "RVG")?.notes || ""}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "RVG" ? { ...param, notes: e.target.value } : param
                  );
                  return updated;
                })
              }
              placeholder="Enter specific RVG requirements, positioning notes, or additional details..."
            />
          </Box>
        )}
      </Grid2>

      {/* CBCT Section */}
      <Grid2 size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={currentParameters.some((param) => param.value === "CBCT" && param?.selected)}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "CBCT" ? { ...param, selected: e.target.checked } : param
                  );
                  return updated;
                })
              }
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight="bold">
              CBCT (Cone Beam CT)
            </Typography>
          }
        />

        {currentParameters.find((param) => param.value === "CBCT")?.selected && (
          <Box
            sx={{
              ml: 4,
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              CBCT Details
            </Typography>
            <TextField
              label="Additional CBCT Notes"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={currentParameters.find((param) => param.value === "CBCT")?.notes || ""}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "CBCT" ? { ...param, notes: e.target.value } : param
                  );
                  return updated;
                })
              }
              placeholder="Enter specific CBCT requirements, field of view, or additional details..."
            />
          </Box>
        )}
      </Grid2>

      {/* OPG Section */}
      <Grid2 size={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={currentParameters.some((param) => param.value === "OPG" && param?.selected)}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "OPG" ? { ...param, selected: e.target.checked } : param
                  );
                  return updated;
                })
              }
            />
          }
          label={
            <Typography variant="subtitle1" fontWeight="bold">
              OPG (Orthopantomogram)
            </Typography>
          }
        />

        {currentParameters.find((param) => param.value === "OPG")?.selected && (
          <Box
            sx={{
              ml: 4,
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              OPG Options & Details
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={currentParameters.find((param) => param.value === "OPG")?.subType?.lateralCeph?.selected || false}
                  onChange={(e) =>
                    setCurrentParameters((prev) => {
                      const updated = prev.map((param) =>
                        param.value === "OPG" ? { ...param, subType: { ...param.subType, lateralCeph: { ...param.subType.lateralCeph, selected: e.target.checked } } } : param
                      );
                      return updated;
                    })
                  }
                />
              }
              label="Include Lateral Cephalogram"
              sx={{ mb: 2, display: "block" }}
            />
            <TextField
              label="Additional OPG Notes"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={currentParameters.find((param) => param.value === "OPG")?.notes || ""}
              onChange={(e) =>
                setCurrentParameters((prev) => {
                  const updated = prev.map((param) =>
                    param.value === "OPG" ? { ...param, notes: e.target.value } : param
                  );
                  return updated;
                })
              }
              placeholder="Enter specific OPG requirements, positioning notes, or additional details..."
            />
          </Box>
        )}
      </Grid2>
    </Grid2>
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => setSelectedTooth(null)} 
      variant="outlined" 
      color="inherit"
    >
      Cancel
    </Button>
    <Button
      variant="contained"
      color="secondary"
      onClick={handleSaveInvestigations}
      disabled={
        !currentParameters.some((param) => param?.selected)
      }
    >
      Save
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default InvestigationToothChart;
              